import type { Feature } from '../../app/feature';
import { esc, q } from '../../shared/dom';
import { getLang, setLang, t } from '../../shared/i18n';
import { dateTime, fmt, money } from '../../shared/format';
import { getState } from '../../shared/store/store';
import { eventPrizes, leadById, pendingPrizes, prizeById } from '../../shared/store/selectors';
import { currentEvent, currentUser, logout } from '../../shared/session/session';
import { eligiblePool } from '../../domain/draw-engine';
import { openStage } from '../studio/studio';
import { hostsApi, type HostDetail } from '../hosts/hosts.api';
import type { DrawEvent } from '../../shared/types';
import { serverNow, syncClock } from '../../shared/clock';

/**
 * Painel do anfitrião (influenciador). Ele só tem um sorteio e uma tarefa: conduzir a live.
 * A tela responde ao que ele precisa saber antes de entrar no estúdio — quando é, o que está
 * em jogo, quantos números concorrem, se está tudo pronto, como conduzir — e mostra o que já
 * saiu (resultados) e o que ele já conduziu antes. Nada do backoffice.
 */
export const hostShell: Feature = {
  template: () => `
<div id="host-shell" hidden>
  <div class="host-bar">
    <div class="brand">
      <div class="brand-mark" aria-hidden="true"></div>
      <div class="brand-txt"><b>Rueda de la Fortuna</b><span>Apueste Rápido</span></div>
    </div>
    <div class="spacer" style="flex:1"></div>
    <div class="hb-who">
      <b id="host-name">—</b>
      <span data-i18n="host.role">Anfitrión invitado</span>
    </div>
    <div class="lang" role="group" aria-label="Idioma">
      <button id="lang-es2" aria-pressed="true">ES</button>
      <button id="lang-pt2" aria-pressed="false">PT</button>
    </div>
    <button class="btn btn-sm" id="host-logout" data-i18n="common.logout">Cerrar sesión</button>
  </div>

  <div class="host-page">
    <section class="hcard hero2" id="host-hero">
      <div class="hero2-main">
        <div class="hero2-eyebrow" id="host-chips"></div>
        <h2 id="host-event">—</h2>
        <p id="host-desc"></p>
        <div class="hgate" id="host-gate" hidden>
          <span class="hgate-lbl" id="host-gate-lbl"></span>
          <div class="hgate-clock" id="host-gate-clock" role="timer" aria-live="off"></div>
          <span class="hgate-when" id="host-gate-when"></span>
        </div>
        <div class="hh-cta">
          <button class="btn btn-go" id="host-open" style="padding:13px 30px; font-size:15px">▶ Abrir el estudio en vivo</button>
          <a class="btn btn-sm" id="host-live" target="_blank" rel="noopener" hidden data-i18n="host.watchLive">Ver transmisión</a>
          <span class="hdone" id="host-done" hidden>✓ <span data-i18n="host.concluded">Sorteo concluido</span></span>
          <div class="hready-status" id="host-check"></div>
        </div>
      </div>
      <aside class="hero2-when" id="host-when"></aside>
    </section>

    <div class="host-grid" id="host-grid">
      <section class="hcard span-8">
        <header><h3 data-i18n="host.prizesTitle">Premios de la noche</h3><span id="host-prizes-sum"></span></header>
        <ol class="hprizes" id="host-prizes"></ol>
      </section>

      <section class="hcard span-4">
        <header><h3 data-i18n="host.poolTitle">Bolón de este sorteo</h3></header>
        <div class="hstats" id="host-pool"></div>
        <p class="hint" id="host-pool-rule"></p>
      </section>

      <section class="hcard span-12" id="host-results-card" hidden>
        <header><h3 data-i18n="host.resultsTitle">Resultados de esta transmisión</h3><span id="host-results-sum"></span></header>
        <ul class="hresults" id="host-results"></ul>
      </section>

      <section class="hcard span-12" id="host-how-card">
        <header><h3 data-i18n="host.howTitle">Cómo conducir la live</h3></header>
        <ol class="hsteps">
          <li data-i18n="host.step1"></li>
          <li data-i18n="host.step2"></li>
          <li data-i18n="host.step3"></li>
        </ol>
      </section>

      <section class="hcard span-12">
        <header><h3 data-i18n="host.historyTitle">Tus transmisiones anteriores</h3><span id="host-history-sum"></span></header>
        <ul class="hhistory" id="host-history"></ul>
      </section>
    </div>
  </div>
</div>`,

  mount() {
    q('#host-logout').onclick = logout;
    q('#host-open').onclick = openStage;
    q('#lang-es2').onclick = () => setLang('es');
    q('#lang-pt2').onclick = () => setLang('pt');
    // contagem regressiva: o relógio anda a cada segundo; quando chega a hora, o botão libera sozinho
    void syncClock();
    let lastKind = '';
    setInterval(() => {
      if (q('#host-shell').hidden) return;
      const g = gateState();
      if (g.kind !== lastKind) { lastKind = g.kind; hostShell.render(); return; }
      renderGate(g);
    }, 1000);
    setInterval(() => { if (!q('#host-shell').hidden) renderChips(); }, 30_000);
  },

  render() {
    const u = currentUser();
    if (!u || u.role !== 'HOST') return;
    const apiEvent = u.event;
    const ev = currentEvent();   // o sorteio precisa existir também no estado do estúdio

    q('#host-name').textContent = u.name;
    q('#host-event').textContent = apiEvent ? apiEvent.name : t('host.noevent');
    q('#lang-es2').setAttribute('aria-pressed', String(getLang() === 'es'));
    q('#lang-pt2').setAttribute('aria-pressed', String(getLang() === 'pt'));
    renderChips();

    const pend = ev ? pendingPrizes(ev.id) : [];
    const pool = eligiblePool(getState(), ev);
    const players = new Set(pool.map(c => c.leadId)).size;
    const draws = ev ? getState().draws.filter(d => d.eventId === ev.id).sort((a, b) => a.at - b.at) : [];
    const done = ev?.status === 'done';

    q('#host-desc').textContent = !apiEvent ? t('host.noeventLead') : done ? t('host.doneLead') : t('host.desc');

    // ---- pronto para a live? uma linha ao lado do botão; o que falta, em laranja
    const missing: string[] = [];
    // sorteio já realizado não tem "falta": só avisamos o que impede uma live que ainda vai acontecer
    if (ev && !done && !pend.length) missing.push(t('host.chkPrizes').toLowerCase());
    if (ev && !done && !pool.length) missing.push(t('host.chkPool').toLowerCase());
    if (ev && !done && !apiEvent?.scheduledAt) missing.push(t('host.chkDate').toLowerCase());
    // só aparece quando falta algo (pronto = silêncio; o botão verde já diz tudo)
    const status = q('#host-check');
    status.className = 'hready-status ko';
    status.innerHTML = ev && missing.length ? `<i></i>${esc(`${t('host.missing')}: ${missing.join(', ')}`)}` : '';
    const over = done || ev?.status === 'cancelled';
    // o estúdio só abre no horário do sorteio (hora do servidor)
    const gate = gateState();
    const locked = gate.kind !== 'open';
    q<HTMLButtonElement>('#host-open').disabled = !ev || !pend.length || over || locked;
    q('#host-open').textContent = locked && ev && !over ? t('host.lockedBtn') : t('host.open');
    q('#host-open').classList.toggle('is-locked', locked);
    q('#host-gate').hidden = !ev || over;
    renderGate(gate);
    q('#host-open').hidden = over;
    q('#host-done').hidden = !over;
    const live = q<HTMLAnchorElement>('#host-live');
    live.hidden = !ev?.liveUrl;
    if (ev?.liveUrl) live.href = ev.liveUrl;

    // ---- prêmios, em ordem de sorteio
    const prizes = ev ? eventPrizes(ev.id).filter(p => p.status !== 'cancelled') : [];
    const total = prizes.reduce((s, p) => s + (p.value || 0), 0);
    q('#host-prizes-sum').textContent = prizes.length ? `${t('host.pozo')} ${money(total)}` : '';
    q('#host-prizes').innerHTML = prizes.length ? prizes.map((p, i) => {
      const d = draws.find(x => x.prizeId === p.id);
      const sub = d
        ? `${esc(t('host.drawn'))} · <b>${esc(d.number)}</b> · ${esc(leadById(d.leadId)?.name ?? '—')}`
        : `${i + 1}º · ${esc(t('host.pending'))}`;
      return `<li class="${d ? 'drawn' : ''}">
        <span class="hp-emoji">${esc(p.emoji || '🎁')}</span>
        <span><span class="hp-name">${esc(p.name)}</span><div class="hp-sub">${sub}</div></span>
        <span class="hp-val">${p.value > 0 ? money(p.value) : esc(t('ev.noValue'))}</span>
      </li>`;
    }).join('') : `<li class="hempty">${esc(t('host.needPrizes'))}</li>`;

    // ---- bolão
    if (done) {
      // o ciclo já foi consumido: o bolão vivo é zero por definição; mostramos o que PARTICIPOU
      const participated = draws[0]?.poolSize ?? 0;
      q('#host-pool').innerHTML = `
        <div><b>${fmt(participated)}</b><span>${esc(t('host.participated'))}</span></div>
        <div><b>${fmt(draws.length)}</b><span>${esc(t('host.delivered'))}</span></div>`;
      q('#host-pool-rule').textContent = t('host.poolDone');
    } else {
      q('#host-pool').innerHTML = `
        <div><b>${fmt(pool.length)}</b><span>${esc(t('stage.cartelas'))}</span></div>
        <div><b>${fmt(players)}</b><span>${esc(t('stage.players'))}</span></div>`;
      q('#host-pool-rule').textContent = !ev ? '' : pool.length === 0 ? t('host.poolEmpty') : ev.tier === 'semanal' ? t('host.poolWeekly') : t('host.poolMonthly');
    }

    // ---- resultados desta transmissão
    q('#host-results-card').hidden = draws.length === 0;
    q('#host-results-sum').textContent = draws.length ? `${draws.length}/${prizes.length}` : '';
    q('#host-results').innerHTML = draws.map((d, i) => {
      const p = prizeById(d.prizeId);
      const name = leadById(d.leadId)?.name ?? '—';
      return `<li class="hr${i === 0 ? ' hr-first' : ''}">
        <span class="hr-num">${d.number.split('').map(ch => `<b>${ch}</b>`).join('')}</span>
        <span class="hr-body">
          <span class="hr-kicker">🏆 ${esc(t('st.winner'))} · ${i + 1}º ${esc(t('stage.prizeN').toLowerCase())}</span>
          <b class="hr-name">${esc(name)}</b>
          <span class="hr-prize">${esc(p?.emoji || '🎁')} ${esc(p?.name ?? '—')}${p && p.value > 0 ? ` · <em>${money(p.value)}</em>` : ''}</span>
          <span class="hr-when">${esc(dateTime(d.at))}</span>
        </span>
      </li>`;
    }).join('');

    // ---- histórico (da API, uma vez por sessão)
    renderHistory(u.id, ev);
  }
};

// ------------------------------------------------------------------ pedaços

/** Data + estado do sorteio (contagem regressiva / ao vivo / encerrado) + tipo. */
function renderChips(): void {
  const u = currentUser();
  if (!u || u.role !== 'HOST') return;
  const apiEvent = u.event;
  const ev = currentEvent();
  // estado da live: ao vivo / encerrado / contagem regressiva
  let state = '';
  // "ao vivo" só depois do horário: antes dele vale a contagem regressiva, mesmo que o status já esteja LIVE
  if (ev?.status === 'live' && gateState().kind !== 'wait') state = `<span class="chip chip-live">● ${esc(t('host.live'))}</span>`;
  else if (ev?.status === 'done') state = `<span class="chip chip-dim">${esc(t('host.done'))}</span>`;
  else if (apiEvent?.scheduledAt) state = `<span class="chip ${countdownClass(apiEvent.scheduledAt)}">${esc(countdownText(apiEvent.scheduledAt))}</span>`;

  // painel de data e hora: é a informação mais importante para o anfitrião, por isso grande e à parte
  const when = q('#host-when');
  if (apiEvent?.scheduledAt) {
    const d = new Date(apiEvent.scheduledAt);
    const loc = getLang() === 'pt' ? 'pt-BR' : 'es-CL';
    const dow = d.toLocaleDateString(loc, { weekday: 'long' });
    const mon = d.toLocaleDateString(loc, { month: 'long' });
    const time = d.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit', hour12: false });
    when.innerHTML = `
      <span class="hw-dow">${esc(dow)}</span>
      <b class="hw-day">${d.getDate()}</b>
      <span class="hw-mon">${esc(mon)} ${d.getFullYear()}</span>
      <i class="hw-line" aria-hidden="true"></i>
      <b class="hw-time">${esc(time)}</b>
      <span class="hw-lbl">${esc(t('host.timeLabel'))}</span>
      ${state ? `<div class="hw-state">${state}</div>` : ''}`;
  } else {
    when.innerHTML = apiEvent ? `<b class="hw-time">—</b><span class="hw-lbl">${esc(t('host.noDate'))}</span>${state ? `<div class="hw-state">${state}</div>` : ''}` : '';
  }

  // linha acima do título: tipo do sorteio + o que esta tela é
  q('#host-chips').innerHTML = ev
    ? `<span class="chip">${esc(t(ev.tier === 'semanal' ? 'tier.weekly' : 'tier.monthly'))}</span><span>${esc(t(ev.status === 'done' ? 'host.eyebrowDone' : 'host.eyebrow'))}</span>`
    : '';
}

function whenText(iso: string): string {
  return new Date(iso).toLocaleString(getLang() === 'pt' ? 'pt-BR' : 'es-CL', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function countdownText(iso: string): string {
  const ms = new Date(iso).getTime() - serverNow();
  if (ms <= 0) return t('host.startable');
  const min = Math.floor(ms / 60_000);
  if (min < 60) return t('host.inMinutes').replace('{m}', String(min));
  const h = Math.floor(min / 60);
  if (h < 24) return t('host.inHours').replace('{h}', String(h)).replace('{m}', String(min % 60));
  const days = Math.floor(h / 24);
  return days === 0 ? t('host.today') : t('host.inDays').replace('{n}', String(days));
}

function countdownClass(iso: string): string {
  const ms = new Date(iso).getTime() - serverNow();
  return ms <= 0 ? 'chip-green' : ms < 3 * 3600_000 ? 'chip-orange' : '';
}

// ------------------------------------------------------------------ abertura do estúdio

type Gate = { kind: 'nodate' } | { kind: 'wait'; ms: number; at: number } | { kind: 'open'; at: number };

/** Pode entrar no estúdio? Só a partir do horário do sorteio, na hora do servidor. */
function gateState(): Gate {
  const iso = currentUser()?.event?.scheduledAt;
  if (!iso) return { kind: 'nodate' };
  const at = Date.parse(iso);
  const ms = at - serverNow();
  return ms > 0 ? { kind: 'wait', ms, at } : { kind: 'open', at };
}

/** Contagem regressiva grande no topo do painel (dias · horas · min · seg). */
function renderGate(g: Gate): void {
  const box = q('#host-gate');
  if (box.hidden) return;
  box.className = `hgate hgate-${g.kind}`;
  const lbl = q('#host-gate-lbl'), clock = q('#host-gate-clock'), when = q('#host-gate-when');
  if (g.kind === 'nodate') {
    lbl.textContent = t('host.gateTitle');
    clock.innerHTML = '';
    when.textContent = t('host.gateNoDate');
    return;
  }
  const loc = getLang() === 'pt' ? 'pt-BR' : 'es-CL';
  const whenTxt = new Date(g.at).toLocaleString(loc, { weekday: 'long', day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit', hour12: false });
  if (g.kind === 'open') {
    lbl.textContent = t('host.gateOpen');
    clock.innerHTML = '';
    when.textContent = '';
    return;
  }
  const s = Math.floor(g.ms / 1000);
  const parts = [
    [Math.floor(s / 86400), t('host.cdDays')],
    [Math.floor(s / 3600) % 24, t('host.cdHours')],
    [Math.floor(s / 60) % 60, t('host.cdMin')],
    [s % 60, t('host.cdSec')],
  ] as const;
  lbl.textContent = t('host.gateTitle');
  // sem o bloco de dias quando falta menos de um dia
  const shown = parts[0][0] > 0 ? parts : parts.slice(1);
  clock.innerHTML = shown.map(([n, u]) => `<div><b>${String(n).padStart(2, '0')}</b><span>${esc(u)}</span></div>`).join('');
  when.textContent = t('host.gateAt').replace('{when}', whenTxt);
}

let history: HostDetail | null = null;
let historyFor: string | null = null;
let historyLoading: Promise<void> | null = null;

/** Sorteios anteriores do anfitrião (a ficha dele na API), sem o sorteio atual. */
function renderHistory(userId: string, ev: DrawEvent | null): void {
  if (historyFor !== userId) { history = null; historyFor = userId; historyLoading = null; }
  if (!history && !historyLoading) {
    historyLoading = hostsApi.me().then(h => { history = h; hostShell.render(); }).catch(() => { historyLoading = null; });
  }
  const past = (history?.events ?? []).filter(e => e.id !== ev?.id && !e.isCurrent);
  const sum = q('#host-history-sum');
  const list = q('#host-history');
  if (!history) { sum.textContent = ''; list.innerHTML = ''; return; }
  sum.textContent = past.length
    ? t('host.historySum').replace('{n}', String(past.length)).replace('{d}', String(history.drawsCount))
      + (history.prizesTotal > 0 ? ` · ${t('host.historyValue').replace('{v}', money(history.prizesTotal))}` : '')
    : '';
  list.innerHTML = past.length ? past.map(e => `<li>
      <span><b>${esc(e.name)}</b><span>${esc(t(e.tier === 'SEMANAL' ? 'tier.weekly' : 'tier.monthly'))}${e.scheduledAt ? ` · ${esc(whenText(e.scheduledAt))}` : ''} · ${e.drawsCount} ${esc(t('host.prizes'))}</span></span>
      <span class="hh-val">${e.prizesTotal > 0 ? money(e.prizesTotal) : ''}</span>
    </li>`).join('') : `<li class="hempty">${esc(t('host.historyEmpty'))}</li>`;
}
